import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search as SearchIcon, Store, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { BusinessCard } from "@/components/BusinessCard";
import { CardsSkeleton } from "@/components/CardsSkeleton";
import { Button } from "@/components/ui/button";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";
  const city = params.get("city") || localStorage.getItem("oyebest_city") || "etah";
  const initialTab = params.get("tab") === "services" ? "services" : "businesses";
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState(initialTab);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  useSeo(`Search: ${q || "all"} | OyeBest`, `Search results for ${q} on OyeBest.`);

  const run = useCallback(() => {
    setLoading(true);
    api.get("/search", { params: { q, city } }).then((r) => setResults(r.data)).finally(() => setLoading(false));
  }, [q, city]);

  useEffect(() => { run(); }, [run]);
  useEffect(() => { setTab(params.get("tab") === "services" ? "services" : "businesses"); }, [params]);

  const businesses = results.filter((r) => r.listing_type !== "service");
  const services = results.filter((r) => r.listing_type === "service");
  const active = tab === "services" ? services : businesses;

  const submit = (e) => { e.preventDefault(); setParams({ q: input, city, tab }); };
  const switchTab = (t) => { setTab(t); const p = { city, tab: t }; if (q) p.q = q; setParams(p); };

  const TABS = [
    { id: "businesses", label: "Businesses", icon: Store, count: businesses.length },
    { id: "services", label: "Services", icon: Wrench, count: services.length },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <form onSubmit={submit} className="bg-white rounded-2xl p-2 flex gap-2 shadow-md max-w-2xl border border-border mb-5">
        <div className="flex items-center flex-1 px-3">
          <SearchIcon className="w-5 h-5 text-muted-foreground" />
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Search businesses or services…" className="w-full outline-none px-3 py-2.5" data-testid="search-page-input" />
        </div>
        <Button type="submit" className="rounded-xl px-6 font-semibold" data-testid="search-page-btn">Search</Button>
      </form>

      <div className="flex gap-2 mb-6" data-testid="search-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => switchTab(t.id)} data-testid={`search-tab-${t.id}`}
            className={`flex items-center gap-2 px-5 h-11 rounded-full text-sm font-semibold border transition-colors ${tab === t.id ? "bg-primary text-white border-primary" : "bg-white border-border text-muted-foreground hover:border-primary/40"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
            <span className={`text-xs rounded-full px-2 py-0.5 ${tab === t.id ? "bg-white/20" : "bg-secondary"}`}>{loading ? "…" : t.count}</span>
          </button>
        ))}
      </div>

      <h1 className="font-display text-2xl sm:text-3xl font-bold">{q ? `Results for "${q}"` : `All ${tab === "services" ? "Services" : "Businesses"}`}</h1>
      <p className="text-muted-foreground mt-1 mb-8">{loading ? "Searching…" : `${active.length} ${tab === "services" ? "services" : "businesses"} in ${city}`}</p>

      {loading ? <CardsSkeleton /> : active.length === 0 ? (
        <div className="text-center py-20 rounded-2xl bg-secondary/50 border border-border" data-testid="search-empty">
          {tab === "services" ? <Wrench className="w-10 h-10 text-muted-foreground mx-auto mb-3" /> : <SearchIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />}
          <h3 className="font-display text-xl font-bold">No {tab === "services" ? "services" : "businesses"} found</h3>
          <p className="text-muted-foreground mt-1">Help your city grow — add the {tab === "services" ? "service" : "business"} you were looking for.</p>
          <Button className="mt-5 rounded-full" onClick={() => navigate(tab === "services" ? "/list-service" : "/list-business")} data-testid="search-add-btn">
            {tab === "services" ? "List Your Service" : "Add This Business"}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {active.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
        </div>
      )}
    </div>
  );
}
